import { type ComponentProps, useId } from 'react';

import { cssMap } from '@atlaskit/css';
import { IconTile } from '@atlaskit/icon';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { MetricText } from '@atlaskit/primitives/compiled/metric-text';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

type AssessmentOverviewCardProps = {
	label: string;
	value: string;
	appearance: 'neutral' | 'warning' | 'success';
	icon: ComponentProps<typeof IconTile>['icon'];
	iconAppearance: ComponentProps<typeof IconTile>['appearance'];
	isLoading: boolean;
	testId: string;
};

const styles = cssMap({
	card: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		minWidth: 0,
	},
	metricContent: { minWidth: 0, flexGrow: 1 },
	neutral: { color: token('color.text') },
	warning: { color: token('color.text.warning') },
	success: { color: token('color.text.success') },
});

export const AssessmentOverviewCard = ({
	label,
	value,
	appearance,
	icon,
	iconAppearance,
	isLoading,
	testId,
}: AssessmentOverviewCardProps) => {
	const labelId = useId();

	return (
		<Inline xcss={styles.card} alignBlock="center" spread="space-between" space="space.150">
			<Stack space="space.050" xcss={styles.metricContent}>
				<Text size="small" id={labelId}>
					{label}
				</Text>
				{isLoading ? (
					<Skeleton
						width="min(140px, 100%)"
						height="28px"
						borderRadius={token('radius.full')}
						testId={`${testId}-skeleton`}
					/>
				) : (
					<Box xcss={styles[appearance]} role="group" aria-labelledby={labelId}>
						<MetricText size="medium">{value}</MetricText>
					</Box>
				)}
			</Stack>
			<IconTile
				size="medium"
				label=""
				icon={icon}
				appearance={iconAppearance}
				testId={`${testId}-icon`}
			/>
		</Inline>
	);
};
